<template>
  <section class="c-section c-section__addkeyword">
    <div class="l-block">
      <div class="l-block__header">
        <TitleSub icon="competitiveKeyword" level="h2">
          <span class="title">キーワード登録</span>
        </TitleSub>
      </div>
      <div class="inbox-area">
        <div class="l-block__header">
          <TitleSub level="h3">
            <span class="title">調査したいキーワード</span>
          </TitleSub>
        </div>
        <div class="c-alertPos">
          <Input
            v-model="params.keyword"
            placeholder="（例）seo対策 キーワード"
            :disabled="isProcessing"
            :class="{
              alert: isEdited.keyword && error.keyword
            }"
            v-focus
            @focusout="focusoutKeyword"
            @enter="registerKeyword"
          />
          <Alert v-if="isEdited.keyword && error.keyword">
            {{ error.keyword }}
          </Alert>
          <Alert v-if="errorResponse">
            {{ errorResponse }}
          </Alert>
        </div>
        <Button
          size="w230"
          fsize="f16"
          variant="main"
          :disabled="isError || isProcessing"
          :class="{ 'is-loading': isProcessing }"
          @click="registerKeyword"
        >
          登録する
        </Button>
      </div>
      <div class="table-area">
        <div class="p-competing__header">
          <div class="l-block__header">
            <TitleSub level="h3">
              <span class="title">登録キーワード一覧</span>
            </TitleSub>
          </div>
          <dl v-if="usage.analysis.max" class="c-list--usage l-ml-2">
            <dt class="c-list--usage__title">
              {{ usage.analysis.label }}
              <Tooltip>
                上限は月単位のもので、毎月1日に登録回数がリセットされます。<br />登録回数はサイト単位でカウントされます。
              </Tooltip>
            </dt>
            <dd class="c-list--usage__content">
              <span
                class="c-list--usage__current"
                :class="{ 'is-over': isAnalyssisUsegeOver }"
                >{{ usage.analysis.current }}</span
              ><span>/</span
              ><span class="c-list--usage__max">{{ usage.analysis.max }}</span>
            </dd>
          </dl>
        </div>
        <table class="c-table">
          <thead>
            <tr>
              <ThSort
                v-model="params.sort"
                name="keyword"
                orderby="asc"
                type="string"
                >登録キーワード</ThSort
              >
              <ThSort
                v-model="params.sort"
                name="sortStartDate"
                orderby="desc"
                type="date"
                width="160"
                >分析データ登録日</ThSort
              >
              <th class="btnTable">
                状態
              </th>
              <th width="60">削除</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="4">
                <Loading variant="inline" />
              </td>
            </tr>
            <tr v-else-if="!keyword.items.length">
              <td class="l-p-0" colspan="4">
                <Error img="require" variant="inline">
                  <template slot="title">
                    キーワードを登録してください。
                  </template>
                </Error>
              </td>
            </tr>
            <template v-else>
              <ListItemCompetitiveKeyword
                v-for="item in sortedItems"
                :key="item.id"
                :item="item"
                :project="project"
                @delete="deleteItem"
                @reload="reloadItem"
                @reloadUsage="getAnalysisUsages"
              />
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import { USAGE_LABEL, USAGE_LABEL_DETAIL } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Error from '@/components/Error';
import Input from '@/components/Input';
import ListItemCompetitiveKeyword from '@/components/competitiveKeyword/ListItemCompetitiveKeyword';
import Loading from '@/components/Loading';
import TitleSub from '@/components/TitleSub';
import Tooltip from '@/components/Tooltip';
import utils from '@/utils';
import Validation from '@/validation';
import apiProjects from '@/api/projects';
import ThSort from '@/components/ThSort';
import { MAX_DATE } from '@/constants';

export default {
  props: ['project'],

  components: {
    Alert,
    Button,
    Error,
    Input,
    ListItemCompetitiveKeyword,
    Loading,
    TitleSub,
    Tooltip,
    ThSort
  },

  data() {
    return {
      error: {
        keyword: ''
      },
      errorResponse: '',
      keyword: {
        items: []
      },
      params: {
        sort: {
          name: 'sortStartDate',
          orderby: 'desc',
          type: 'date'
        },
        keyword: ''
      },
      isEdited: {
        keyword: false
      },
      isError: false,
      isLoading: true,
      isProcessing: false,
      reloadTimer: false,
      usage: {
        analysis: {
          current: null,
          label: USAGE_LABEL_DETAIL['competitor-analyses'],
          max: null
        }
      }
    };
  },

  computed: {
    keywordNames() {
      return this.keyword.items.map(item => {
        return item.keyword.toLowerCase();
      });
    },
    isAnalyssisUsegeOver() {
      return this.usage.analysis.current >= this.usage.analysis.max;
    },
    sortedItems() {
      const items = this.keyword.items.slice();
      items.forEach(item => {
        item.sortStartDate = item.startDate ? item.startDate : MAX_DATE;
      });
      return utils.sort(
        items,
        this.params.sort.type,
        this.params.sort.orderby,
        this.params.sort.name
      );
    }
  },

  watch: {
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponse = '';
      },
      deep: true
    },
    'params.keyword': {
      handler(value, oldValue) {
        let keyword = this.$options.filters.replaceDoubleSpaceToSpace(
          this.params.keyword.trim()
        );
        this.error.keyword = new Validation()
          .input()
          .maxLength(100)
          .keyword()
          .notRegistered(
            this.keywordNames,
            'すでに登録されているキーワードです'
          )
          .test(keyword);
        this.isEdited.keyword = keyword ? true : false;
      },
      immediate: true
    }
  },

  created() {
    this.getKeywords();
    this.getAnalysisUsages();
  },

  beforeDestroy() {
    clearTimeout(this.reloadTimer);
  },

  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },

    getKeywords() {
      let params = {
        limit: 1000,
        offset: 0
      };

      apiProjects.researchKeywords
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          this.keyword.items = response.data.items;
          this.isLoading = false;
        })
        .catch(error => {
          this.isLoading = false;
        });
    },

    getUsages() {
      const params = {
        items: 'research-keywords'
      };
      return apiProjects.usages.get(params, this.auth.idToken, this.project.id);
    },

    // 分析回数を取得
    getAnalysisUsages() {
      const params = {
        items: 'competitor-analyses'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          this.usage.analysis.current = response.data.items[0].current;
          this.usage.analysis.max = response.data.items[0].max;
        });
    },

    focusoutKeyword() {
      this.params.keyword = this.$options.filters.replaceDoubleSpaceToSpace(
        this.params.keyword
      );
    },

    registerKeyword() {
      if (this.isError || this.isProcessing) {
        return;
      }
      this.isProcessing = true;

      // 利用量を取得
      this.getUsages()
        .then(response => {
          let current = response.data.items[0].current;
          let max = response.data.items[0].max;
          if (current >= max) {
            this.errorResponse = `${USAGE_LABEL['research-keywords']}の登録上限数を超えています`;
            this.isProcessing = false;
            return;
          }
          let params = {
            keyword: this.params.keyword
          };
          apiProjects.researchKeywords
            .post(params, this.auth.idToken, this.project.id)
            .then(response => {
              this.getKeywords();
              this.isProcessing = false;
              this.params.keyword = '';
              this.isEdited.keyword = false;
            })
            .catch(error => {
              if (error.response) {
                this.errorResponse = error.response.data.errors[0].userMessage;
              }
              this.isProcessing = false;
            });
        })
        .catch(error => {
          if (error.response) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          }
          this.isProcessing = false;
        });
    },

    deleteItem(item) {
      for (let i = 0; i < this.keyword.items.length; i++) {
        if (this.keyword.items[i].id === item.id) {
          this.keyword.items.splice(i, 1);
          break;
        }
      }
      this.getAnalysisUsages();
    },
    reloadItem() {
      let reload = () => {
        let cancelFlag = true;
        this.reloadTimer = setTimeout(reload, 600000);
        this.getKeywords();

        for (let i = 0; i < this.keyword.items.length; i++) {
          if (
            this.keyword.items[i].isSuggestGetable === false ||
            this.keyword.items[i].isCompetitorRankGetable === false
          ) {
            cancelFlag = false;
            break;
          }
        }
        if (cancelFlag) {
          clearTimeout(this.reloadTimer);
        }
      };
      if (!this.reloadTimer) {
        reload();
      }
    }
  }
};
</script>
